import { formatMoney, formatDate } from '../lib/format';
import {
  useTransactionList,
  TransactionListToolbar,
  StatusBadge,
  Pager,
  ListStates,
  SortableTh,
  type StatusOption,
} from './TransactionList';
import { CustomerFilter, DateRangeFilter } from './TransactionFilters';
import { ViewHeader } from './DashboardLayout';
import { useConnection, stripeRefundUrl, hubspotContactUrl } from './links';
import { ExtLink } from './ExtLink';
import type { RefundListEntry } from './types';

/**
 * Dashboard → Refunds (M19 T2 + T3).
 *
 * Money-out ledger: one row per Stripe Refund. Filterable by status
 * ("pending" = bank-delay watch, "failed" = stuck), the universal Customer
 * typeahead, and a date-range preset. The parent charge links to Stripe
 * (refunds have no standalone dashboard page).
 */

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'succeeded', label: 'Succeeded', tone: 'success' },
  { value: 'pending', label: 'Pending', tone: 'brand' },
  { value: 'requires_action', label: 'Requires action', tone: 'warn' },
  { value: 'failed', label: 'Failed', tone: 'danger' },
  { value: 'canceled', label: 'Canceled', tone: 'neutral' },
];

export function Refunds() {
  const list = useTransactionList<RefundListEntry>('admin/refunds', 'status');
  const {
    data,
    loading,
    error,
    refetch,
    status,
    setStatus,
    customer,
    setCustomer,
    dateRange,
    setDateRange,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Refunds"
        subtitle="Stripe Refunds — money out and stuck/failed refunds."
      />
      <TransactionListToolbar
        status={status}
        setStatus={setStatus}
        statusOptions={STATUS_OPTIONS}
        statusLabel="Status"
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CustomerFilter
          selected={customer}
          selectedLabel={null}
          onSelect={(id) => {
            setCustomer(id);
            setOffset(0);
          }}
          onClear={() => setCustomer(null)}
        />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Refund</th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <SortableTh
                    label="Created"
                    sortKey="created"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((r) => (
                  <RefundRow key={r.id} refund={r} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function RefundRow({ refund }: { refund: RefundListEntry; }) {
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeRefundUrl(stripeAccountId, refund.stripeChargeId);
  const hsUrl = hubspotContactUrl(portalId, refund.customerEmail ?? '');
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        <div className="flex flex-col">
          {stripeUrl ? (
            <ExtLink href={stripeUrl} label={refund.id} title="View charge in Stripe" />
          ) : (
            <span className="text-xs text-ink-600">{refund.id}</span>
          )}
          <span className="text-xs text-ink-400">{refund.stripeChargeId}</span>
        </div>
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col">
          <span className="text-ink-800">{refund.customerName ?? '—'}</span>
          {refund.customerEmail && (
            <span className="text-xs text-ink-400">
              {hsUrl ? (
                <ExtLink href={hsUrl} label={refund.customerEmail} title="View in HubSpot" />
              ) : (
                refund.customerEmail
              )}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-right text-ink-800">
        {formatMoney(refund.amountMinor, refund.currency)}
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={refund.status} options={STATUS_OPTIONS} />
        {refund.status === 'failed' && refund.failureReason && (
          <div className="mt-0.5 text-[10px] text-danger-600">{refund.failureReason}</div>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-ink-600">{refund.reason ?? '—'}</td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500" title={formatDate(refund.createdAt)}>
        {formatDate(refund.createdAt)}
      </td>
    </tr>
  );
}
